import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, iif, of, EMPTY } from 'rxjs';
import { Action } from '@ngrx/store';
import { switchMap, map, catchError, take } from 'rxjs/operators';
import isEmpty from 'lodash-es/isEmpty';

import { AppFacade } from '@app/app.facade';
import * as actions from '@recent-searches/store/actions/recent-searches.actions';
import { RecentService } from '@recent-searches/services/recent.service';
import { RecentSearchModel } from '@recent-searches/entities/recent-search';
import { RecentSearchDataType } from '@design-system/components/molecules/recent-search/entities/recent-search.types';
import { SearchParamFromType } from '@search-bar/entities/search-type';

@Injectable()
export class RecentSearchesEffect {
  constructor(
    private readonly action$: Actions,
    private readonly service: RecentService,
    private readonly appFacade: AppFacade,
  ) { }

  fetchRecentSearches$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(actions.fetchRecentSearchesAction),
      switchMap(() => this.service.fetchRecentSearches()),
      map((response) => actions.fetchRecentSearchesPlaceMapperSuccessAction({ response })),
      catchError(() => EMPTY)
    ));

  deleteRecentSearches$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(actions.deleteRecentSearchesAction),
      switchMap(() => this.service.deleteRecentSearches()),
      map(() => actions.deleteRecentSearchesSuccessAction()),
      catchError(() => EMPTY)
    ));

  fetchRecentSearchesPlaceMapperSuccess$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(actions.fetchRecentSearchesPlaceMapperSuccessAction),
      switchMap(action => {
        return [
          actions.fetchRecentSearchesSuccessAction({ response: action.response })
        ];
      }),
      catchError(() => EMPTY)
    ));

  saveRecentSearch$: Observable<Action> = createEffect(() => this.action$
    .pipe(
      ofType(actions.saveRecentSearchAction),
      map(action => {
        let serviceSaved: () => Observable<RecentSearchModel> = () => of({} as RecentSearchModel);
        switch (action.dataType) {
          case RecentSearchDataType.Flight:
            serviceSaved = () => this.service.saveFlightSearch(action.payload);
            break;
          case RecentSearchDataType.Hotel:
            serviceSaved = () => this.service.saveHotelSearch(action.payload);
            break;
          case RecentSearchDataType.Bus:
            serviceSaved = () => this.service.saveBusSearch(action.payload);
            break;
          case RecentSearchDataType.Cars:
            serviceSaved = () => this.service.saveCarsSearch(action.payload);
            break;
          case RecentSearchDataType.Packages:
            serviceSaved = () => this.service.saveAirPackageSearch(action.payload);
            break;
        }
        return { serviceSaved, from: action.from };
      }),
      switchMap((data: {
        serviceSaved: () => Observable<RecentSearchModel>, from: SearchParamFromType,
      }) => this.appFacade.user$
        .pipe(
          take(1),
          switchMap(user => iif(
            () => !!user?.id && !!data?.serviceSaved && data?.from !== SearchParamFromType.RECENT,
            data.serviceSaved().pipe(
              switchMap((saved) => {
                return !isEmpty(saved) ? [actions.saveRecentSearchSuccessAction()] : [];
              }),
              catchError(() => EMPTY)
            ),
            EMPTY
          ))
        )
      )
    ));
}
